import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MapPin, ChevronRight, Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Physical hierarchy levels (Site -> Building -> Floor -> Zone -> Space).
const LEVELS = [
  { kind: 'site', field: 'siteId', label: 'Site' },
  { kind: 'building', field: 'buildingId', label: 'Building' },
  { kind: 'floor', field: 'floorId', label: 'Floor' },
  { kind: 'zone', field: 'zoneId', label: 'Zone' },
  { kind: 'space', field: 'spaceId', label: 'Space' },
];

/**
 * Reusable, touch-friendly construction context selector.
 * Cascades Site -> Building -> Floor -> Zone -> Space and a Trade.
 * Remembers the last selection per tenant+user+project in localStorage
 * ("Smart Context Memory"), never leaking across tenants/projects, and discards
 * stale (deleted/archived) node ids. Reads the live hierarchy (no mock data).
 */
const ConstructionContextSelector = ({ projectId, value, onChange }) => {
  const { user } = useAuth();
  const memKey = `rvx_ctx_${user?.tenantId || 't'}_${user?.id || user?.userId || 'u'}_${projectId}`;
  const [locations, setLocations] = useState([]);
  const [trades, setTrades] = useState([]);
  const [ctx, setCtx] = useState(value || {});
  const [loaded, setLoaded] = useState(false);

  const emit = useCallback((next) => {
    setCtx(next);
    try { localStorage.setItem(memKey, JSON.stringify(next)); } catch (e) { /* */ }
    if (onChange) onChange(next);
  }, [memKey, onChange]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [locRes, trRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/construction/locations`),
          axios.get(`${API}/projects/${projectId}/construction/trades`),
        ]);
        if (!active) return;
        const locs = locRes.data || [];
        const trs = trRes.data || [];
        setLocations(locs);
        setTrades(trs);
        // Smart Context Memory: restore last selection only when none is provided,
        // dropping any id that no longer exists (deleted/archived).
        if (!value || Object.keys(value).length === 0) {
          let saved = null;
          try { saved = JSON.parse(localStorage.getItem(memKey) || 'null'); } catch (e) { saved = null; }
          if (saved && typeof saved === 'object') {
            const locIds = new Set(locs.map((l) => l.id));
            const trIds = new Set(trs.map((t) => t.id));
            const cleaned = {};
            Object.entries(saved).forEach(([f, v]) => {
              if (f === 'tradeId') { if (trIds.has(v)) cleaned[f] = v; }
              else if (locIds.has(v)) cleaned[f] = v;
            });
            setCtx(cleaned);
            if (onChange) onChange(cleaned);
            try { localStorage.setItem(memKey, JSON.stringify(cleaned)); } catch (e) { /* */ }
          }
        }
      } catch (e) {
        /* selector stays empty if hierarchy unavailable */
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => { active = false; };
  }, [projectId, memKey]);

  const candidatesFor = (levelIdx) => {
    const level = LEVELS[levelIdx];
    let list = locations.filter((l) => l.kind === level.kind);
    for (let i = levelIdx - 1; i >= 0; i--) {
      const sel = ctx[LEVELS[i].field];
      if (sel) { list = list.filter((l) => l.parentId === sel); break; }
    }
    return list;
  };

  const updateLevel = (levelIdx, val) => {
    const level = LEVELS[levelIdx];
    const next = { ...ctx };
    if (val) next[level.field] = val; else delete next[level.field];
    // Clear descendant location levels when a higher level changes.
    LEVELS.slice(levelIdx + 1).forEach((l) => delete next[l.field]);
    emit(next);
  };

  const updateTrade = (val) => {
    const next = { ...ctx };
    if (val) next.tradeId = val; else delete next.tradeId;
    emit(next);
  };

  const clearAll = () => emit({});

  const nameOf = (id) => (locations.find((l) => l.id === id) || {}).name;
  const tradeName = (id) => (trades.find((t) => t.id === id) || {}).name;

  const visibleLevels = LEVELS
    .map((lvl, idx) => ({ lvl, idx, candidates: candidatesFor(idx) }))
    .filter((x) => x.candidates.length > 0 || ctx[x.lvl.field]);

  const hasStructure = locations.length > 0 || trades.length > 0;
  const hasSelection = Object.keys(ctx).length > 0;

  if (!loaded) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm text-slate-400" data-testid="context-selector-loading">
        Loading construction context…
      </div>
    );
  }

  if (!hasStructure) {
    return (
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-slate-600 flex items-start gap-2" data-testid="context-selector-empty">
        <Layers className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <span>No construction structure configured for this project yet. A CompanyAdmin can set it up under <span className="font-medium">Construction Structure</span> to tag captures to a physical location.</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-3" data-testid="context-selector">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
          <MapPin className="w-4 h-4 text-blue-600" /> Construction Context
        </div>
        {hasSelection && (
          <button onClick={clearAll} className="text-xs text-slate-400 hover:text-red-500" data-testid="context-clear-btn">Clear</button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visibleLevels.map(({ lvl, idx, candidates }) => (
          <div key={lvl.field}>
            <label className="text-xs text-slate-500">{lvl.label}</label>
            <select
              value={ctx[lvl.field] || ''}
              onChange={(e) => updateLevel(idx, e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
              data-testid={`context-select-${lvl.kind}`}
            >
              <option value="">— {lvl.label} —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        ))}

        {trades.length > 0 && (
          <div>
            <label className="text-xs text-slate-500">Trade</label>
            <select
              value={ctx.tradeId || ''}
              onChange={(e) => updateTrade(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
              data-testid="context-select-trade"
            >
              <option value="">— Trade —</option>
              {trades.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {hasSelection && (
        <div className="flex items-center flex-wrap gap-1 text-xs text-slate-600 pt-1" data-testid="context-breadcrumb">
          {LEVELS.filter((l) => ctx[l.field]).map((l, i, arr) => (
            <span key={l.field} className="flex items-center gap-1">
              <span className="px-2 py-0.5 bg-slate-100 rounded-full">{nameOf(ctx[l.field])}</span>
              {i < arr.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            </span>
          ))}
          {ctx.tradeId && (
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full ml-1">{tradeName(ctx.tradeId)}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ConstructionContextSelector;
